import React, { useEffect, useRef } from 'react'
import HelpTutorial, { HelpTutorialSlide } from '@fs/zion-help-tutorial'
import { useTranslation } from 'react-i18next'
import { H1, Paragraph, Separator } from '@fs/zion-ui'
import slide1 from '../../help/attachFlow/HistoricalDocumentIllustration.svg'
import slide2 from '../../help/attachFlow/IndexingIllustration.svg'
import slide3 from '../../help/attachFlow/AttachImageIllustration.svg'

const AttachFlowTutorial = ({ helpTutorial }) => {
  const { t } = useTranslation()
  const hasOpened = useRef(false)

  // Open the tutorial on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run only once on mount; guarded by hasOpened
  useEffect(() => {
    if (!hasOpened.current) {
      hasOpened.current = true
      helpTutorial.open()
    }
  }, [])

  return (
    <HelpTutorial
      tutorialId="fullText.attachFlow.tutorial"
      tutorialTitle={t('attachFlow.tutorial.title', 'Attach to Family Tree')}
      tutorialDescription={t('attachFlow.tutorial.description', 'Learn how to use this new feature')}
      endHeader={false}
      {...helpTutorial}
    >
      {/* Slide 1 */}
      <HelpTutorialSlide
        imageSrc={slide1}
        imgBleed={false}
        imageAlt={t('attachFlow.tutorial.slide1.alt', 'Computer with document')}
        heading={
          <>
            <Separator spacing="xl" />
            <H1 size="H4">{t('attachFlow.tutorial.slide1.title', 'A new way to build and improve your tree')}</H1>
          </>
        }
        content={
          <>
            <Separator spacing="md" />
            <Paragraph size="md">
              {t(
                'attachFlow.tutorial.slide1.content',
                'The information from this historical document contains so much untapped potential.'
              )}
            </Paragraph>
          </>
        }
        imageProps={{
          style: {
            padding: 16,
          },
        }}
      />

      {/* Slide 2 */}
      <HelpTutorialSlide
        imageSrc={slide2}
        imgBleed={false}
        imageAlt={t('attachFlow.tutorial.slide2.alt', 'Magnifying glass on document')}
        heading={
          <>
            <Separator spacing="xl" />
            <H1 size="H4">
              {t('attachFlow.tutorial.slide2.title', 'Start by indexing the information found on this image')}
            </H1>
          </>
        }
        content={
          <>
            <Separator spacing="md" />
            <Paragraph size="md">
              {t(
                'attachFlow.tutorial.slide2.content.part1',
                "The Full-Text transcript isn't in the same format as the data in Family Tree. With this new process you can bridge that gap by adding names and events to accurately build and improve your tree."
              )}
            </Paragraph>
          </>
        }
        imageProps={{
          style: {
            padding: 16,
          },
        }}
      />

      {/* Slide 3 */}
      <HelpTutorialSlide
        imageSrc={slide3}
        imgBleed={false}
        imageAlt={t('attachFlow.tutorial.slide3.alt', 'People working together')}
        heading={
          <>
            <Separator spacing="xl" />
            <H1 size="H4">{t('attachFlow.tutorial.slide3.title', 'Short on time?')}</H1>
          </>
        }
        content={
          <>
            <Separator spacing="md" />
            <Paragraph size="md">
              {t(
                'attachFlow.tutorial.slide3.content',
                "If you aren't able to index the information right now, you can still attach this image. That will save it to Sources, but won't add any information to the tree."
              )}
            </Paragraph>
          </>
        }
        imageProps={{
          style: {
            padding: 16,
          },
        }}
      />
    </HelpTutorial>
  )
}

export default AttachFlowTutorial
